import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const CreateInvoice = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    clientName: '', clientEmail: '', dueDate: '', notes: '', tax: 0
  });
  const [items, setItems] = useState([{ description: '', quantity: 1, rate: 0 }]);

  const addItem = () => setItems([...items, { description: '', quantity: 1, rate: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => {
    const updated = [...items];
    updated[i][field] = value;
    setItems(updated);
  };

  const subtotal = items.reduce((s, it) => s + (it.quantity * it.rate), 0);
  const taxAmount = (subtotal * (form.tax || 0)) / 100;
  const total = subtotal + taxAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post('https://mevteck.com/invoice-api', 
        { ...form, items },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Create Invoice</h2>
        <button style={styles.backBtn} onClick={() => navigate('/dashboard')}>← Back</button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Client Info</div>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Client Name</label>
              <input style={styles.input} value={form.clientName}
                onChange={e => setForm({...form, clientName: e.target.value})}
                placeholder="Yaseen Ansari" required />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Client Email</label>
              <input style={styles.input} type="email" value={form.clientEmail}
                onChange={e => setForm({...form, clientEmail: e.target.value})}
                placeholder="client@email.com" required />
            </div>
          </div>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Due Date</label>
              <input style={styles.input} type="date" value={form.dueDate}
                onChange={e => setForm({...form, dueDate: e.target.value})} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Tax %</label>
              <input style={styles.input} type="number" value={form.tax}
                onChange={e => setForm({...form, tax: e.target.value})}
                min="0" max="100" />
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>Items</div>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Description', 'Qty', 'Rate (USD)', 'Amount', ''].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i}>
                  <td style={styles.td}>
                    <input style={styles.input} value={item.description}
                      onChange={e => updateItem(i, 'description', e.target.value)}
                      placeholder="Service description" required />
                  </td>
                  <td style={styles.td}>
                    <input style={{...styles.input, width: '70px'}} type="number"
                      value={item.quantity} min="1"
                      onChange={e => updateItem(i, 'quantity', parseFloat(e.target.value))} />
                  </td>
                  <td style={styles.td}>
                    <input style={{...styles.input, width: '100px'}} type="number"
                      value={item.rate} min="0" step="0.01"
                      onChange={e => updateItem(i, 'rate', parseFloat(e.target.value))} />
                  </td>
                  <td style={{...styles.td, color: '#fff'}}>
                    ${(item.quantity * item.rate).toFixed(2)}
                  </td>
                  <td style={styles.td}>
                    {items.length > 1 && (
                      <button type="button" style={styles.delBtn} onClick={() => removeItem(i)}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" style={styles.addBtn} onClick={addItem}>+ Add Item</button>

          <div style={styles.totals}>
            <div style={styles.totalRow}><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
            <div style={styles.totalRow}><span>Tax ({form.tax}%)</span><span>${taxAmount.toFixed(2)}</span></div>
            <div style={{...styles.totalRow, ...styles.grandTotal}}>
              <span>Total</span><span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>Notes</div>
          <textarea style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
            value={form.notes}
            onChange={e => setForm({...form, notes: e.target.value})}
            placeholder="Payment instructions, thank you message..." />
        </div>

        <button style={styles.submitBtn} type="submit" disabled={loading}>
          {loading ? 'Creating Invoice...' : 'Generate Invoice & PayPal Link'}
        </button>
      </form>
    </div>
  );
};

const styles = {
  container: { margin: '0 auto', padding: '2rem', background: '#0f0f0f', minHeight: '100vh', color: '#fff' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title: { fontSize: '22px', fontWeight: '700', color: '#fff' },
  backBtn: { background: 'none', border: '1px solid #333', color: '#aaa', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  error: { background: '#2a1a1a', color: '#ff6b6b', padding: '10px', borderRadius: '8px', marginBottom: '1rem', fontSize: '14px' },
  card: { background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '1.5rem', marginBottom: '1rem' },
  sectionTitle: { fontSize: '13px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  field: { marginBottom: '0.5rem' },
  label: { display: 'block', color: '#aaa', fontSize: '13px', marginBottom: '6px' },
  input: { width: '100%', padding: '9px 12px', background: '#111', border: '1px solid #333', borderRadius: '8px', color: '#fff', fontSize: '14px', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: '12px', color: '#888', padding: '8px 4px', borderBottom: '1px solid #2a2a2a' },
  td: { padding: '8px 4px' },
  delBtn: { background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '16px' },
  addBtn: { background: 'none', border: 'none', color: '#4da6ff', cursor: 'pointer', fontSize: '14px', padding: '8px 0', marginTop: '8px' },
  totals: { textAlign: 'right', marginTop: '1rem', borderTop: '1px solid #2a2a2a', paddingTop: '1rem' },
  totalRow: { display: 'flex', justifyContent: 'flex-end', gap: '2rem', fontSize: '14px', color: '#aaa', marginBottom: '6px' },
  grandTotal: { fontSize: '18px', fontWeight: '700', color: '#fff', marginTop: '8px' },
  submitBtn: { width: '100%', padding: '14px', background: '#fff', color: '#000', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '0.5rem' }
};

export default CreateInvoice;